import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ShieldAlert } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { TRIAGEM_BTN } from '@/components/kanban/triagem/triagemTabela';
import { bkoVeSoOsSeus } from '@/lib/fila-permissions';
import type { Segmento } from '@/components/kanban/triagem/segmentoTriagem';

interface Pendente {
  pedido_id: string;
  numero_pedido: string | null;
  razao_social: string | null;
  consultor_nome: string | null;
  valor_renovacao: number | null;
  bko_id: string | null;
  bko_nome: string | null;
  mes_trabalho: string | null;
}

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * A TABELA DA RENOVAÇÃO É DO BKO DONO DO PEDIDO (Douglas 22/10/2026: "cada bko
 * responsável pelos pedidos tem que ter visão e informar qual é a tabela").
 * A fila é a view `v_conferencia_tabela_renovacao`: renovação da Diretoria com a
 * tabela 1/2 ainda não decidida. BKO_* vê os pedidos dele e os sem dono; a
 * supervisora e a gestão veem todos. A lista fica no topo da Triagem Pedido
 * Fechado mesmo com a fila vazia (o modal só mostra "Nenhum pedido pendente"
 * quando esta lista também está vazia).
 */
export function useConferenciaTabelaRenovacao(segmento: Segmento) {
  const { profile } = useAuth();
  const ehCnpj = segmento === 'PJ';
  const soOsMeus = bkoVeSoOsSeus(profile as { funcao?: string | null } | null);
  return useQuery({
    queryKey: ['conferencia-tabela-renovacao', profile?.id, soOsMeus],
    enabled: !!profile?.id && ehCnpj,
    staleTime: 15_000,
    queryFn: async () => {
      let q = supabase.from('v_conferencia_tabela_renovacao')
        .select('pedido_id, numero_pedido, razao_social, consultor_nome, valor_renovacao, bko_id, bko_nome, mes_trabalho')
        .order('mes_trabalho', { ascending: false })
        .order('numero_pedido');
      q = await aplicarEscopoTriagem(q, profile);
      if (soOsMeus) q = q.or(`bko_id.eq.${profile!.id},bko_id.is.null`);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as Pendente[];
    },
  });
}

export function ConferenciaTabelaRenovacao({ segmento }: { segmento: Segmento }) {
  const { open: abrirPedido } = usePainelPedido();
  const ehCnpj = segmento === 'PJ';
  const { data: pendentes = [] } = useConferenciaTabelaRenovacao(segmento);

  if (!ehCnpj || pendentes.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg border border-border bg-card shadow-[inset_3px_0_0_hsl(var(--primary))]">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <ShieldAlert className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Convergência a conferir · tabela da renovação</span>
        <span className="text-xs text-muted-foreground">{pendentes.length} pedidos da Diretoria com a tabela da renovação sem decisão: enquanto não é decidida, a renovação não paga. Abra o pedido e defina Tabela 1 ou 2 na Conferência BKO.</span>
      </div>
      <div className="max-h-[220px] overflow-auto">
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col className="w-[96px]" />
            <col />
            <col className="w-[150px]" />
            <col className="w-[120px]" />
            <col className="w-[64px]" />
            <col className="w-[96px]" />
            <col className="w-[52px]" />
          </colgroup>
          <tbody>
            {pendentes.map((p) => (
              <tr key={p.pedido_id} className="border-t border-border first:border-t-0">
                <td className="px-4 py-1.5 font-mono">{p.numero_pedido}</td>
                <td className="truncate px-2 py-1.5" title={p.razao_social ?? ''}>{p.razao_social}</td>
                <td className="truncate px-2 py-1.5 text-muted-foreground" title={p.consultor_nome ?? ''}>{p.consultor_nome}</td>
                <td className="truncate px-2 py-1.5 text-muted-foreground" title="BKO dono do pedido">{p.bko_nome || 'sem dono'}</td>
                <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{p.mes_trabalho ? `${p.mes_trabalho.slice(5, 7)}/${p.mes_trabalho.slice(0, 4)}` : '—'}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{moeda.format(Number(p.valor_renovacao) || 0)}</td>
                <td className="px-4 py-1.5 text-right">
                  <button
                    type="button"
                    className={TRIAGEM_BTN}
                    title="Conferir convergência no pedido"
                    aria-label="Conferir convergência no pedido"
                    onClick={() => abrirPedido(p.pedido_id, 'visao360')}
                  >
                    <ArrowUpRight />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
